import React, { useContext, useState, memo, useCallback, useMemo } from "react";
import styles from "./ResourceMonitor.module.scss";
import {
  AddIconSvgBlack,
  ArrowForwardIcon,
  BackIcon,
  CalendarIcon,
  DeleteIcon,
  EditIcon,
  MeatBallsMenuIconVertical,
  MeetingsClockIcon,
  MiniCalendarSvg,
  NotFoundIcon,
} from "../../../modules/default-svg";
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import Button from "../../../components/controls/button";
import InputUnderline from "../../../components/controls/input/InputUnderline";
import CustomInput from "../../../components/controls/input/customInput/CustomInput";
import { ResourcePlannerContext } from "./ResourcePlannerContext";
import {
  tConverter,
} from "../../../helpers/utils";
import Modal from "../../../components/controls/modal/Modal";
import NotFoundLayout from "../../../components/controls/NotFound/NotFound";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";

const TimeOffCard = memo(() => {
  const {
    calendarData,
    detail,
    tabView,
    useDetails,
    createTimeOff,
    deleteTimeOff,
  } = useContext(ResourcePlannerContext);
  
  const permissions = localStorage.getItem("permissions");
  const dispatch = useDispatch();
  const [addTimeOffView, setAddTimeOffView] = useState(false);
  const [editTimeOff, setEditTimeOff] = useState(false);
  const [name, setName] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [showDeleteModal, setShowDeleteModel] = useState(false);
  const [timeOffId, setTimeOffId] = useState("");

  const handleCreateTimeOff = useCallback(async () => {
    let error = "";
    if (!startDate) {
      error = "Start date is a required field";
    } else if (!endDate) {
      error = "End date is a required field";
    } else if (!startTime) {
      error = "Start time is a required field";
    } else if (!endTime) {
      error = "End time is a required field";
    } else if (!name) {
      error = "Name is a required field";
    } else if (
      new Date(startDate).toISOString() === new Date(endDate).toISOString()
    ) {
      const start = new Date(`1970-01-01T${startTime}:00Z`);
      let end = new Date(`1970-01-01T${endTime}:00Z`);
      if (end <= start) {
        error = "Start time can not be greater than end time.";
      }
    } else if (new Date(startDate) > new Date(endDate)) {
      error = "Start date can not be grater than End Date.";
    }

    if (error) {
      return dispatch(
        generalActions.showToast({
          title: error,
          type: "error",
        })
      );
    }
    
    let stDate = new Date(startDate);
    let edDate = new Date(endDate);
    if (stDate) {
      stDate = stDate.setDate(stDate.getDate() + 1);
    }
    if (edDate) {
      edDate = edDate.setDate(edDate.getDate() + 1);
    }
    
    try {
      const res = await createTimeOff({
        Name: name,
        StartDate: `${new Date(stDate).toISOString().split("T")[0]}T${startTime}`,
        EndDate: `${new Date(edDate).toISOString().split("T")[0]}T${endTime}`,
        Type: 1,
        Status: 1,
        Reason: "",
        ResourceId: useDetails[detail.userIndex].id,
      });
      
      if (res?.status < 202) {
        setAddTimeOffView(false);
        dispatch(
          generalActions.showToast({
            title: `Timeoff ${editTimeOff ? "updated" : "created"} successfully.`,
            type: "success",
          })
        );
      }
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error.message || "Failed to create time off",
          type: "error",
        })
      );
    }
  }, [name, startDate, startTime, endDate, endTime, useDetails, detail, createTimeOff, editTimeOff, dispatch]);

  const handleDeleteTimeOff = useCallback(async () => {
    try {
      await deleteTimeOff(timeOffId);
      dispatch(
        generalActions.showToast({
          title: "Timeoff deleted successfully.",
          type: "success",
        })
      );
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error.message || "Failed to delete time off",
          type: "error",
        })
      );
    }
  }, [timeOffId, deleteTimeOff, dispatch]);

  const timeOffsData = useMemo(() => {
    if (!calendarData || !detail || !calendarData[detail.userIndex]) {
      return [];
    }

    if (tabView !== "hour") {
      const cellData = calendarData[detail.userIndex][detail.ind]?.[0];
      return cellData?.timeOffs || cellData?.timeoffs || [];
    }

    return calendarData[detail.userIndex][detail.hourlyIdentifier]?.[0]?.timeOffs || [];
  }, [calendarData, detail, tabView]);

  const ExampleContent = useCallback(({ item }) => {
    return (
      <div className="listingPopoverWrapper">
        {!showDeleteModal && (
          <div className="listingPopoverContent">
            {permissions.includes("Leave.Edit") && (
              <div className="popItem">
                <EditIcon />
                <p
                  className="text-sm-400 ms-12"
                  onClick={() => {
                    setAddTimeOffView(true);
                    setEditTimeOff(true);
                    setStartTime(tConverter(item.startTime));
                    setEndTime(tConverter(item.endTime));
                    setStartDate(item.startDate);
                    setEndDate(item.endDate);
                    setName(item.name);
                  }}
                >
                  Edit
                </p>
              </div>
            )}
            {permissions.includes("Leave.Delete") && (
              <div
                className="popItem"
                onClick={() => {
                  setTimeOffId(item.id);
                  setShowDeleteModel(true);
                }}
              >
                <DeleteIcon />
                <p className="text-sm-400 ms-12">Delete</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [showDeleteModal, permissions]);

  return (
    <>
      {showDeleteModal && (
        <Modal
          title={`Delete Timeoff`}
          body={
            <p className="text-sm-400 subHeading-color">{`Are you sure you want to delete the selected timeoff?`}</p>
          }
          handleCancel={() => setShowDeleteModel(false)}
          handleSave={() => {
            setShowDeleteModel(false);
            handleDeleteTimeOff();
          }}
          saveBtnLabel={`Delete`}
          cancelBtnLabel={"Cancel"}
          isLoading={false}
        />
      )}
      {!addTimeOffView ? (
        <div className={styles.TimeOffScroll}>
          <div className={styles.TaskCardContainer}>
            <div className={styles.cardSubHeader}>
              <h5>Time Off</h5>
              {permissions.includes("Leave.Create") && (
                <Button
                  btnClass="d_btn d_btn_whiteXsm"
                  icon={<AddIconSvgBlack />}
                  label={"Add"}
                  clickHandler={() => setAddTimeOffView(true)}
                />
              )}
            </div>
            {timeOffsData.length > 0 ? (
              <div>
                {timeOffsData.map((item) => (
                  <div className={styles.timeOffMain} key={item.id}>
                    <div className="d-flex justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <h5 className="font-weight-500">{item.name}</h5>
                        <div className={styles.capacity}>
                          <MiniCalendarSvg />
                          <div className="text-xsm-600 calanderHeadercolor">
                            {item.startDate
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("/")}
                            <span className="text-xsm-700 greyStrokeColor">
                              {" "}
                              .{" "}
                              {JSON.parse(localStorage.getItem("settings"))
                                .regionalSettings.timeFormat === "hh:mm a"
                                ? tConverter(item.startTime)
                                : item.startTime}
                            </span>{" "}
                            -{" "}
                            {item.endDate
                              .split("T")[0]
                              .split("-")
                              .reverse()
                              .join("/")}
                            <span className="text-xsm-700 greyStrokeColor">
                              {" "}
                              .{" "}
                              {JSON.parse(localStorage.getItem("settings"))
                                .regionalSettings.timeFormat === "hh:mm a"
                                ? tConverter(item.endTime)
                                : item.endTime}
                            </span>
                          </div>
                        </div>
                      </div>
                      {(permissions.includes("Leave.Edit") ||
                        permissions.includes("Leave.Delete")) && (
                        <Popover positioning="below-end">
                          <PopoverTrigger disableButtonEnhancement>
                            <span>
                              <MeatBallsMenuIconVertical />
                            </span>
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <ExampleContent item={item} />
                          </PopoverSurface>
                        </Popover>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <NotFoundLayout
                icon={<NotFoundIcon />}
                heading="No data currently available!"
                para=""
              />
            )}
          </div>
        </div>
      ) : (
        <div className={styles.TaskCardContainer}>
          <div
            className="d-flex align-items-center cursor-pointer gap-1 mb-3"
            onClick={() => {
              setAddTimeOffView(false);
              setEditTimeOff(false);
              setStartDate(null);
              setEndDate(null);
              setStartTime(null);
              setEndTime(null);
              setName(null);
            }}
          >
            <BackIcon />
            <span className="text-xsm-400 AvatarColor">Back to time off</span>
          </div>
          <div>
            <div className={styles.CalendarSubModalScroll}>
              <h4 className="mb-3">Edit Time off</h4>
              <InputUnderline
                placeholder="Time Off"
                handleChange={setName}
                value={name}
                isRequired
              />
              <div className="d-flex align-items-start mb-3 mt-3">
                <div className="d-flex align-items-end">
                  <CustomInput
                    inputwd="w-50 me-2"
                    value={startDate}
                    icon={<CalendarIcon />}
                    type="date"
                    handleChange={setStartDate}
                    label="Start Date & Time"
                    isRequired
                  />
                  <CustomInput
                    inputwd="w-40"
                    value={startTime}
                    icon={<MeetingsClockIcon />}
                    type="time"
                    handleChange={setStartTime}
                    label=""
                    placeholder="days"
                  />
                </div>
                <div className="ArrowInputIcon mt-4">
                  <ArrowForwardIcon />
                </div>
                <div className="d-flex align-items-end justify-content-end">
                  <CustomInput
                    inputwd="w-50 me-2"
                    value={endDate}
                    icon={<CalendarIcon />}
                    type="date"
                    handleChange={setEndDate}
                    label="End Date & Time"
                    isRequired
                  />
                  <CustomInput
                    inputwd="w-40"
                    value={endTime}
                    icon={<MeetingsClockIcon />}
                    type="time"
                    handleChange={setEndTime}
                    label=""
                    placeholder="days"
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end mt-3">
                <Button
                  btnClass="d_btn d_btn_primary"
                  label="Save Time off"
                  clickHandler={handleCreateTimeOff}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

TimeOffCard.displayName = 'TimeOffCard';

export default TimeOffCard;
